'use client'

import { Button } from '@vinted/web-ui'

import { useEffect } from 'react'

import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { urlWithParams } from '@marketplace-web/browser/url-util'
import { useRefUrl } from '@marketplace-web/ref-url/ref-url-feature'

import { markAsUnreserved } from '@marketplace-web/item-seller-action-buttons/item-action-buttons-data'

import { useTrackAbTestCallback, useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { ItemPageMarkAsReservedButtonPluginModel } from './types'
import { MARK_ITEM_AS_RESERVED_URL } from '../../constants/routes'

type Props = {
  data: ItemPageMarkAsReservedButtonPluginModel | undefined
}

const ItemPageMarkAsReservedButtonPlugin = ({ data }: Props) => {
  const translate = useTranslate('item')
  const refUrl = useRefUrl()
  const { user: currentUser } = useSession()

  const trackAbTest = useTrackAbTestCallback()
  const removeReserveAbTest = useAbTest('remove_reserve')
  const shouldRemoveReserveButton = removeReserveAbTest?.variant === 'on'

  useEffect(() => {
    if (!data || data.isItemReserved) return

    trackAbTest(removeReserveAbTest)
  }, [trackAbTest, removeReserveAbTest, data])

  if (!data) return null

  const { itemId, isItemReserved, canReserve, transactionPermitted, userId } = data
  const isViewingOwnItem = userId === currentUser?.id

  if (!isViewingOwnItem) return null
  if (!(canReserve && transactionPermitted)) return null

  const handleClick = async () => {
    await markAsUnreserved({ itemId })

    reloadPage()
  }

  if (isItemReserved) {
    return (
      <Button
        text={translate('actions.mark_as_unreserved')}
        styling={Button.Styling.Outlined}
        testId="mark-as-unreserved-button"
        size={Button.Size.Medium}
        onClick={handleClick}
      />
    )
  }

  const markAsReservedUrl = urlWithParams(MARK_ITEM_AS_RESERVED_URL(itemId), {
    ref_url: refUrl,
  })

  if (shouldRemoveReserveButton) return null

  return (
    <Button
      text={translate('actions.mark_as_reserved')}
      styling={Button.Styling.Outlined}
      url={markAsReservedUrl}
      testId="mark-as-reserved-button"
      size={Button.Size.Medium}
    />
  )
}

export default ItemPageMarkAsReservedButtonPlugin
